import { useState, useEffect } from "preact/hooks";
import { API_BASE } from "../lib/api-base.js";

export interface PreChatFormConfig {
  enabled: boolean;
  allowSkip: boolean;
  title: string;
  subtitle: string;
  fields: { name: string; label: string; type: string; required: boolean }[];
}

export type WidgetTheme = "auto" | "light" | "dark";

export interface WidgetConfig {
  tenantId: string;
  botName: string;
  botAvatar: string | null;
  primaryColor: string;
  welcomeMessage: string;
  position: "bottom-right" | "bottom-left";
  bottomOffset?: number;
  sideOffset?: number;
  language: string;
  theme: WidgetTheme;
  preChatForm: PreChatFormConfig | null;
  /** Email domains rejected in the pre-chat form (also enforced server-side). */
  blockedEmailDomains?: string[];
  suggestedQuestions: string[];
  followUpSuggestions: boolean;
  disclaimer: string | null;
  privacyPolicyUrl: string | null;
  hideBranding: boolean;
  proactiveMessages: {
    id: string;
    message: string;
    urlPattern: string;
    delaySeconds: number;
  }[];
  whatsapp: {
    enabled: boolean;
    phoneNumber: string;
    prefillMessage?: string;
  } | null;
  bookingUrl: string | null;
  salesCta: {
    enabled: boolean;
    afterRounds: number;
    label: string;
    target: "auto" | "booking" | "whatsapp";
  } | null;
}

/** Apply theme by toggling .light/.dark class on <html>. "auto" = media query decides. */
export function applyWidgetTheme(theme: WidgetTheme) {
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  if (theme === "light") root.classList.add("light");
  else if (theme === "dark") root.classList.add("dark");
}

export async function fetchConfig(slug: string): Promise<WidgetConfig> {
  const res = await fetch(`${API_BASE}/api/widget/config/${slug}`);
  if (!res.ok) {
    let message = "Failed to load widget config.";
    try {
      const body = await res.json();
      if (body.message) message = body.message;
    } catch {}

    // Surface error to host page via postMessage
    if (window.parent !== window) {
      window.parent.postMessage({ type: "canary:error", message }, "*");
    }
    throw new Error(message);
  }
  return res.json();
}

export function useConfig(slug: string) {
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }

    fetchConfig(slug)
      .then((data) => {
        // Query param override: ?theme=light|dark|auto (set by loader from data-theme)
        const params = new URLSearchParams(window.location.search);
        const themeOverride = params.get("theme") as WidgetTheme | null;
        const theme = themeOverride || data.theme || "auto";
        const resolved = { ...data, theme };

        setConfig(resolved);
        document.documentElement.style.setProperty("--primary", data.primaryColor);
        applyWidgetTheme(theme);
      })
      .catch((err) => {
        setError(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug]);

  // Listen for dynamic theme changes from host page
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data?.type === "canary:theme") {
        const theme = event.data.theme as WidgetTheme;
        if (theme === "light" || theme === "dark" || theme === "auto") {
          applyWidgetTheme(theme);
          setConfig((prev) => prev ? { ...prev, theme } : null);
        }
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return { config, error, loading };
}
